import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { RawIntlProvider, createIntl, createIntlCache } from "react-intl";
import { flatten } from "../utils";
import { LocalizationProvider } from "./LocalizationProvider";

const intlCache = createIntlCache();
export type MessageType = Record<string, Record<string, string | object>>;

export const LANGUAGES = { it: "it-IT" } as const;

export type TLanguagesKeys = keyof typeof LANGUAGES;
export type TLocaleConfigurations = Record<
  TLanguagesKeys,
  {
    locale: Locale;
    dateFormat: string;
    dateFieldLocaleText: Record<string, string | object>;
  }
>;

type TIntlProviderProps = {
  defaultLanguage?: string;
  messages: Record<string, Record<string, string>>;
  locale?: string;
  intlConfig: TLocaleConfigurations;
  convertToFlattenJson?: boolean;
} & PropsWithChildren;

type TContext = {
  language: string;
  setLanguage?: Dispatch<SetStateAction<string>>;
  intlConfig: TLocaleConfigurations;
  defaultLanguage?: string;
};

export const IntlContext = createContext<TContext>({
  language: "it",
  intlConfig: {} as TLocaleConfigurations,
  defaultLanguage: "it",
});

export const IntlProvider = ({
  locale = "it",
  convertToFlattenJson = true,
  messages,
  intlConfig,
  defaultLanguage = "it",
  children,
}: TIntlProviderProps) => {
  const [language, setLanguage] = useState<string>(locale);
  const rawIntlValue = createIntl(
    {
      locale: language,
      messages: flatten(messages[language], convertToFlattenJson) as Record<
        string,
        string
      >,
    },
    intlCache,
  );

  return (
    <IntlContext.Provider
      value={{
        language,
        setLanguage,
        intlConfig,
        defaultLanguage,
      }}
    >
      <RawIntlProvider value={rawIntlValue}>
        <LocalizationProvider config={intlConfig} locale={locale}>
          {children}
        </LocalizationProvider>
      </RawIntlProvider>
    </IntlContext.Provider>
  );
};

export const useIntlContext = () => useContext(IntlContext);
